/** 알림 (S6) — what the three rules raised after a sync: spend jumping,
 *  revenue falling, an app losing money. Each one is read once and marked.
 *  The server's title is the rule's name ("지출 급증"), so the rule has no
 *  column of its own.
 *
 *  전체 | 안 읽음 sits in the address (`?state=unread`), so the 확인할 것
 *  strip and the sidebar badge can open the unread list directly. The time is
 *  the server's UTC (SQLite text or RFC3339) shown in Seoul time. */

import { Check } from 'lucide-react'
import type { AlertRow } from '@/api/client'
import { useAlerts, useReadAlert } from '@/lib/queries'
import { parseSqliteUtc } from '@/lib/time'
import {
  ButtonLink,
  DataTable,
  EmptyState,
  FilterBar,
  IconButton,
  PageHeader,
  Segmented,
  StatusPill,
  useQueryParam,
  useToast,
  When,
  errorText,
  type Column,
} from '@/ui'

type State = 'all' | 'unread'

export default function Alerts() {
  const alerts = useAlerts()
  const read = useReadAlert()
  const toast = useToast()
  const [param, setParam] = useQueryParam('state', 'all')
  const state: State = param === 'unread' ? 'unread' : 'all'

  const all = alerts.data
  const unread = all?.filter((a) => !a.is_read).length ?? 0
  const rows = state === 'unread' ? all?.filter((a) => !a.is_read) : all

  async function markRead(a: AlertRow) {
    try {
      await read.mutateAsync(a.id)
    } catch (e) {
      toast.error(`읽음 표시 실패: ${errorText(e)}`)
    }
  }

  const columns: Column<AlertRow>[] = [
    {
      key: 'severity',
      header: '심각도',
      card: 'meta',
      width: '6rem',
      cell: (a) => <StatusPill domain="alertSeverity" status={a.severity} />,
    },
    {
      key: 'title',
      header: '알림',
      card: 'title',
      cell: (a) => (
        <span className="flex min-w-0 items-center gap-2">
          {!a.is_read && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-status-scheduled" aria-label="안 읽음" />}
          <span className="break-words font-medium">{a.title}</span>
        </span>
      ),
    },
    {
      key: 'message',
      header: '내용',
      card: 'sub',
      cell: (a) => <span className="break-words text-muted-foreground">{a.message}</span>,
    },
    {
      key: 'at',
      header: '시각',
      card: 'sub',
      cell: (a) => <When iso={parseSqliteUtc(a.created_at)} className="whitespace-nowrap tabular-nums" />,
    },
    {
      key: 'action',
      header: <span className="sr-only">읽음</span>,
      align: 'right',
      // Beside the severity on a phone card: a footer row for one small
      // button made every alert a line longer.
      card: 'meta',
      cell: (a) =>
        a.is_read ? (
          <span className="text-meta text-muted-foreground">읽음</span>
        ) : (
          <IconButton
            icon={Check}
            label="읽음으로 표시"
            size="sm"
            variant="secondary"
            disabled={read.isPending && read.variables === a.id}
            onClick={(e) => {
              e.stopPropagation()
              void markRead(a)
            }}
          />
        ),
    },
  ]

  return (
    <>
      <PageHeader
        title="알림"
        description={all ? (unread > 0 ? `안 읽은 알림 ${unread}건` : '안 읽은 알림 없음') : '지출 급증 · 수익 급락 · 적자 앱'}
        // The strip's "안 읽은 알림" leads here; on this page it would repeat
        // the list below. Its other items (sync, AdMob) stay.
        attentionOmit={['alerts-unread']}
      />
      <FilterBar className="mb-3">
        <Segmented<State>
          label="보기"
          value={state}
          onChange={(v) => setParam(v)}
          options={[
            { value: 'all', label: '전체', count: all?.length },
            { value: 'unread', label: '안 읽음', count: all ? unread : undefined },
          ]}
        />
      </FilterBar>
      <DataTable
        label="알림"
        columns={columns}
        rows={rows}
        rowKey={(a) => a.id}
        loading={alerts.isLoading}
        error={alerts.error}
        onRetry={() => alerts.refetch()}
        rowClassName={(a) => (a.is_read ? 'opacity-60' : undefined)}
        mode="auto"
        empty={
          state === 'unread' && (all?.length ?? 0) > 0 ? (
            <EmptyState
              title="모두 읽었다"
              description="읽은 알림은 전체에 남아 있다."
              actions={<ButtonLink to="/alerts">전체 보기</ButtonLink>}
            />
          ) : (
            <EmptyState
              title="알림 없음"
              description="동기화가 끝날 때마다 지출 급증 · 수익 급락 · 적자 앱 규칙을 확인한다. 걸린 것이 있으면 여기 쌓인다."
            />
          )
        }
      />
    </>
  )
}
